import { useCallback, useEffect, useState } from 'react';
import { inject, observer } from 'mobx-react';

import logo from '../../assets/img/logo-xpens.svg';
import uploadIcon from '../../assets/img/icon-upload.svg';
import fileIcon from '../../assets/img/icon-file.svg';
import closeIcon from '../../assets/img/icon-close.svg';
import fileUploadedIcon from '../../assets/img/icon-file-uploaded.svg';
import uploadImageIcon from '../../assets/img/icon-uploading.svg';
import DragAndDrop from '../../components/drag-drop';
import withRouter from '../../components/withRouter';
import { fetch } from '../../utils/api-wrapper';
import { validateStatementFiles } from './statement-upload-validation';
import './statement-imports.scss';

const STATEMENT_FILE_ACCEPT = '.csv,.pdf,application/pdf,text/csv';

/**
 * Gives statement uploads the same focused, full-page workflow used by invoice
 * and expense uploads. The API still receives the complete statement batch in
 * one request because it validates the files together.
 *
 * The response confirms receipt only: reading and reconciling each statement
 * continues on the API afterwards, so this page reports what was accepted and
 * what was rejected outright, and the statement list reports the outcome.
 *
 * @param {object} props
 * @param {object} props.data - MobX data store containing the translator and menu state.
 * @param {Function} props.navigate - React Router navigation function.
 * @returns {JSX.Element} The statement upload page.
 */
export const StatementAddPage = ({ data, navigate }) => {
  const [files, setFiles] = useState([]);
  const [errorKey, setErrorKey] = useState(null);
  const [step, setStep] = useState('init');
  const [uploadResult, setUploadResult] = useState(null);

  // Upload pages hide the application menu so the user can focus on the file task.
  useEffect(() => {
    data.hideMenu = true;
    return () => {
      data.hideMenu = false;
    };
  }, [data]);

  const addFiles = useCallback((selectedFiles) => {
    const incoming = Array.from(selectedFiles || []).filter((file) => file?.name);
    if (!incoming.length) return;

    // A repeated browser or drag selection must not submit the same physical file twice.
    const existingKeys = new Set(files.map((file) => `${file.name}-${file.size}-${file.lastModified}`));
    const uniqueIncoming = incoming.filter(
      (file) => !existingKeys.has(`${file.name}-${file.size}-${file.lastModified}`)
    );
    const nextFiles = [...files, ...uniqueIncoming];
    const validationError = validateStatementFiles(nextFiles);

    if (validationError) {
      setErrorKey(validationError);
      return;
    }

    setFiles(nextFiles);
    setErrorKey(null);
  }, [files]);

  const removeFile = useCallback((fileToRemove) => {
    setFiles((currentFiles) => currentFiles.filter((file) => file !== fileToRemove));
    setErrorKey(null);
  }, []);

  const submitStatements = () => {
    const validationError = validateStatementFiles(files);
    if (validationError) {
      setErrorKey(validationError);
      return;
    }

    const formData = new FormData();
    files.forEach((file) => formData.append('statements', file));
    setErrorKey(null);
    setStep('uploading');

    fetch(
      '/c_card/statement-imports',
      'POST',
      formData,
      (response) => {
        setUploadResult(response?.data || null);
        setStep('completed');
      },
      () => {
        // Keep the valid selection in place so a temporary failure can be retried.
        setErrorKey('cc_statements.statement_imports.errors.upload_failed');
        setStep('init');
      },
      true
    );
  };

  const filePicker = (translationKey) => (
    <label className='btn btn-primary btn-no-border-radius btn-primary-shadow pl-5 pr-5'>
      {data.t.read(translationKey)}
      <input
        type='file'
        accept={STATEMENT_FILE_ACCEPT}
        multiple
        onChange={(event) => addFiles(event.target.files)}
      />
    </label>
  );

  const statementResults = uploadResult?.statement_results || [];

  const resultDetail = (result) => {
    // Card routing happens during processing, so it is only known for a
    // statement that has already finished -- a duplicate of an earlier upload.
    const label = result.card_account?.label;
    const mask = result.card_account?.masked_number;
    const card = [label, mask].filter(Boolean).join(' · ');
    if (card) return card;
    if (result.status === 'processing') {
      return data.t.read('cc_statements.statement_imports.processing_hint');
    }
    return data.t.read('cc_statements.statement_imports.not_routed');
  };

  return (
    <div className='new-invoice statement-add-page'>
      <div className='position-absolute' style={{ left: 20, top: 20 }}>
        <img src={logo} alt='XPENS logo' />
      </div>
      <button
        type='button'
        className='btn btn-link position-absolute text-primary'
        style={{ right: 20, top: 20 }}
        onClick={() => navigate(-1)}
      >
        {data.t.read('back')}
      </button>

      <div className='page-centered'>
        {step === 'uploading' ? (
          <div className='uploading-container'>
            <img src={uploadImageIcon} alt='' />
            <div className='font-weight-bold font-large mt-3'>
              {data.t.read('cc_statements.statement_imports.uploading')}
            </div>
          </div>
        ) : step === 'completed' ? (
          <div className='uploading-container statement-routing-summary'>
            <img src={fileUploadedIcon} alt='' />
            <div className='font-weight-bold mt-3'>
              {data.t.read('cc_statements.statement_imports.success')}
            </div>
            <div className='font-small semi-faded mt-2'>
              {data.t.read('cc_statements.statement_imports.success_description')}
            </div>
            {statementResults.length > 0 && (
              <div
                className='statement-routing-summary__results mt-4'
                aria-label={data.t.read('cc_statements.statement_imports.routing_summary')}
              >
                {statementResults.map((result, index) => (
                  <div
                    key={result.statement_reconciliation_id || `${result.filename}-${index}`}
                    className='statement-routing-summary__row'
                  >
                    <div className='statement-routing-summary__file'>
                      <div className='font-weight-bold'>{result.filename}</div>
                      <div className='font-small semi-faded'>{resultDetail(result)}</div>
                      {result.status === 'failed' && result.error && (
                        <div className='font-small text-danger mt-1'>
                          {data.t.read(
                            `cc_statements.statement_imports.failure_reasons.${result.error}`
                          )}
                        </div>
                      )}
                    </div>
                    <div className='statement-routing-summary__actions'>
                      <span className={`statement-routing-summary__status statement-routing-summary__status--${result.status}`}>
                        {data.t.read(`cc_statements.statement_imports.status.${result.status}`)}
                      </span>
                    </div>
                  </div>
                ))}
              </div>
            )}
            <button
              type='button'
              className='btn btn-primary btn-no-border-radius btn-primary-shadow pl-5 pr-5 mt-5'
              onClick={() => navigate('/c_card/statements/')}
            >
              {data.t.read('done')}
            </button>
          </div>
        ) : (
          <DragAndDrop handleDrop={addFiles}>
            {files.length ? (
              <div className='new-files-container'>
                <div className='mb-auto full-width'>
                  {files.map((file) => (
                    <div
                      key={`${file.name}-${file.size}-${file.lastModified}`}
                      className='invoice-item d-flex justify-content-between mb-3'
                    >
                      <div className='invoice-file-info'>
                        <div className='font-weight-bold'>{file.name}</div>
                        <div className='font-small semi-faded'>
                          {(file.size / 1000 / 1000).toFixed(2)} {data.t.read('mb_unit')}
                        </div>
                      </div>
                      <button
                        type='button'
                        className='btn btn-link p-0 ml-4'
                        aria-label={`${data.t.read('remove')} ${file.name}`}
                        onClick={() => removeFile(file)}
                      >
                        <img src={closeIcon} alt='' />
                      </button>
                    </div>
                  ))}
                </div>

                <div className='invoice-item p-0'>
                  <label className='pointer p-3 statement-add-page__add-more'>
                    <img src={fileIcon} alt='' className='mr-4' />
                    {data.t.read('cc_statements.statement_imports.add_another')}
                    <input
                      type='file'
                      accept={STATEMENT_FILE_ACCEPT}
                      multiple
                      onChange={(event) => addFiles(event.target.files)}
                    />
                  </label>
                </div>
                <p className='font-small semi-faded mt-3 mb-0'>
                  {data.t.read('cc_statements.statement_imports.supported_files')}
                </p>
                {errorKey && <p role='alert' className='text-danger mt-3'>{data.t.read(errorKey)}</p>}
                <button
                  type='button'
                  className='btn btn-primary btn-no-border-radius btn-primary-shadow pl-5 pr-5 mt-4'
                  onClick={submitStatements}
                >
                  {data.t.read('cc_statements.statement_imports.upload')}
                </button>
              </div>
            ) : (
              <div className='first-files-container'>
                <img src={uploadIcon} alt='' />
                <h1 className='font-weight-bold font-large mt-3'>
                  {data.t.read('cc_statements.statement_imports.page_title')}
                </h1>
                <p className='font-small semi-faded mb-2'>
                  {data.t.read('cc_statements.statement_imports.page_description')}
                </p>
                <p className='font-small semi-faded mb-4'>
                  {data.t.read('cc_statements.statement_imports.supported_files')}
                </p>
                {filePicker('cc_statements.statement_imports.select_files')}
                {errorKey && <p role='alert' className='text-danger mt-3'>{data.t.read(errorKey)}</p>}
              </div>
            )}
          </DragAndDrop>
        )}
      </div>
    </div>
  );
};

export default inject('data')(withRouter(observer(StatementAddPage)));
